import { useCallback, useMemo } from 'react'

import { useQuery } from '@apollo/client/react'

import { usePaginatedContentItems } from '~publish/components/ContentItemCard'
import type { ContentItemDraft } from '~publish/components/ContentItemCard'
import { useOrganizationId } from '~publish/contexts/AccountContext'
import { graphql, readFragment } from '~publish/graphql'
import type { ResultOf } from '~publish/graphql'
import { useChannelAgnosticDraftsEnabled } from '~publish/hooks/useChannelAgnosticDraftsEnabled'

export const LATEST_DRAFTS_LIMIT = 4

export const DraftPost_Post = graphql(`
  fragment DraftPost_Post on Post {
    id
    text
    createdAt
    dueAt
    channel {
      id
      service
      name
      avatar
    }
    metadata {
      __typename
      ... on CommonPostMetadata {
        type
      }
    }
    assets {
      __typename
      thumbnail
      source
    }
  }
`)

export type DraftPost = ResultOf<typeof DraftPost_Post>

export const GetLatestDraftPosts = graphql(
  `
    query GetLatestDraftPosts(
      $organizationId: OrganizationId!
      $first: Int!
      $sort: [PostSortInput!]
    ) {
      posts(
        input: {
          organizationId: $organizationId
          filter: { status: [draft] }
          sort: $sort
        }
        first: $first
      ) {
        edges {
          node {
            ...DraftPost_Post
          }
        }
      }
      postCounts(
        input: { organizationId: $organizationId, filter: { status: [draft] } }
      ) {
        counts {
          channelId
          count
        }
      }
    }
  `,
  [DraftPost_Post],
)

export type LatestDraft =
  | { kind: 'post'; createdAt: string; post: DraftPost }
  | { kind: 'contentItem'; createdAt: string; contentItem: ContentItemDraft }

/**
 * Interleaves channel drafts (Posts) and channel-less drafts (ContentItems)
 * into a single newest-first list. Both inputs are already newest-first, so
 * taking `limit` of each before merging is lossless.
 */
export function mergeLatestDrafts(
  posts: DraftPost[],
  contentItems: ContentItemDraft[],
  limit: number = LATEST_DRAFTS_LIMIT,
): LatestDraft[] {
  const entries: LatestDraft[] = [
    ...posts.map(
      (post): LatestDraft => ({
        kind: 'post',
        createdAt: post.createdAt,
        post,
      }),
    ),
    ...contentItems.map(
      (contentItem): LatestDraft => ({
        kind: 'contentItem',
        createdAt: contentItem.createdAt,
        contentItem,
      }),
    ),
  ]

  return entries
    .toSorted((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt))
    .slice(0, limit)
}

type UseLatestDraftsResult = {
  drafts: LatestDraft[]
  totalCount: number
  loading: boolean
  refetch: () => Promise<unknown>
}

/**
 * The latest drafts of an organization across both kinds: channel drafts
 * (Posts with `draft` status) and channel-less drafts (ContentItems, only
 * when that feature is enabled). `totalCount` sums both sets — `postCounts`
 * counts per channel, so channel-less drafts are invisible to it and come
 * from the ContentItems connection instead.
 *
 * Channel-less drafts come from `GetContentItemList` via
 * `usePaginatedContentItems`. The composer refetches that query by name on
 * save and promote, and the hook applies content item Pusher events.
 */
export const useLatestDrafts = (): UseLatestDraftsResult => {
  const organizationId = useOrganizationId()
  const channelAgnosticDraftsEnabled = useChannelAgnosticDraftsEnabled()

  const { data, loading, refetch } = useQuery(GetLatestDraftPosts, {
    variables: {
      organizationId: organizationId ?? '',
      first: LATEST_DRAFTS_LIMIT,
      sort: [{ field: 'createdAt', direction: 'desc' }],
    },
    skip: !organizationId,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  })

  const {
    draftContentItems,
    totalCount: contentItemsTotalCount,
    loading: contentItemsLoading,
    refetch: refetchContentItems,
  } = usePaginatedContentItems({
    perPage: LATEST_DRAFTS_LIMIT,
    skip: !channelAgnosticDraftsEnabled,
  })

  const refetchAll = useCallback(
    () =>
      Promise.all([
        refetch(),
        channelAgnosticDraftsEnabled ? refetchContentItems() : undefined,
      ]),
    [refetch, refetchContentItems, channelAgnosticDraftsEnabled],
  )

  return useMemo(() => {
    const posts =
      data?.posts?.edges?.map((edge) =>
        readFragment(DraftPost_Post, edge.node),
      ) ?? []
    const contentItems = channelAgnosticDraftsEnabled ? draftContentItems : []
    const postDraftsCount =
      data?.postCounts?.counts?.reduce((sum, { count }) => sum + count, 0) ?? 0

    return {
      drafts: mergeLatestDrafts(posts, contentItems),
      totalCount:
        postDraftsCount +
        (channelAgnosticDraftsEnabled ? contentItemsTotalCount : 0),
      loading: loading || (channelAgnosticDraftsEnabled && contentItemsLoading),
      refetch: refetchAll,
    }
  }, [
    data,
    draftContentItems,
    contentItemsTotalCount,
    channelAgnosticDraftsEnabled,
    loading,
    contentItemsLoading,
    refetchAll,
  ])
}
